// src/api/superAdminService.js - FINAL FIXED VERSION
import {
  signInWithEmailAndPassword,
  signOut
} from 'firebase/auth';
import {
  collection,
  doc,
  getDoc,
  getDocs,
  setDoc,
  updateDoc,
  deleteDoc,
  query,
  where,
  serverTimestamp,
  Timestamp
} from 'firebase/firestore';
import { auth, db } from '../utils/firebase';
import { DEFAULT_PERMISSIONS } from './salonAuthService';

// ========================================
// SUPERADMIN AUTHENTICATION
// ========================================

export const loginSuperAdmin = async (email, password) => {
  try {
    const userCredential = await signInWithEmailAndPassword(auth, email, password);
    const user = userCredential.user;
    
    const superAdminDoc = await getDoc(doc(db, 'superAdmins', user.uid));
    
    if (!superAdminDoc.exists()) {
      await signOut(auth);
      throw new Error('Unauthorized: Not a superadmin account');
    }
    
    return {
      user: {
        uid: user.uid,
        email: user.email,
        ...superAdminDoc.data()
      }
    };
  } catch (error) {
    console.error('Error logging in as superadmin:', error);
    throw error;
  }
};

export const logoutSuperAdmin = async () => {
  try {
    await signOut(auth);
    return true;
  } catch (error) {
    console.error('Error logging out:', error);
    throw error;
  }
};

export const getCurrentSuperAdmin = async () => {
  if (!auth.currentUser) {
    return null;
  }
  
  try {
    const superAdminDoc = await getDoc(doc(db, 'superAdmins', auth.currentUser.uid));
    if (!superAdminDoc.exists()) {
      return null;
    }
    
    return {
      uid: auth.currentUser.uid,
      email: auth.currentUser.email,
      ...superAdminDoc.data()
    };
  } catch (error) {
    console.error('Error getting current superadmin:', error);
    throw error;
  }
};

export const isSuperAdminAuthenticated = async () => {
  if (!auth.currentUser) {
    return false;
  }
  
  try {
    const superAdminDoc = await getDoc(doc(db, 'superAdmins', auth.currentUser.uid));
    return superAdminDoc.exists();
  } catch (error) {
    console.error('Error checking superadmin authentication:', error);
    return false;
  }
};

// ========================================
// CLIENT CONFIGURATION MANAGEMENT
// ========================================

export const createClientConfiguration = async (formData) => {
  try {
    const customDomain = formData.customDomain;
    const salonId = customDomain;
    
    console.log('Creating client configuration with customDomain:', customDomain);
    
    const enabledModulesList = ['home'];
    if (formData.modules.promotions) enabledModulesList.push('promotions');
    if (formData.modules.communications) enabledModulesList.push('communications');
    if (formData.modules.bookings) enabledModulesList.push('bookings');
    if (formData.modules.reports) enabledModulesList.push('reports');
    if (formData.modules.bespokeai) enabledModulesList.push('bespokeai');
    
    const planName = enabledModulesList.join('+');
    
    const enabledModules = {
      home: true,
      promotions: formData.modules.promotions,
      communications: formData.modules.communications,
      bookings: formData.modules.bookings,
      reports: formData.modules.reports,
      bespokeai: formData.modules.bespokeai
    };
    
    const clientEmail = formData.admins && formData.admins[0] ? formData.admins[0].email : '';
    
    await setDoc(doc(db, 'clients', customDomain), {
      salonId: salonId,
      name: formData.salonName,
      status: formData.status || 'active',
      enabledModules: enabledModules,
      clientDomain: customDomain,
      email: clientEmail,
      plan: planName,
      adminLoginURL: '',
      customerLoginURL: '',
      billing: {
        monthlyPrice: '',
        monthlyNettPrice: '',
        discount: '',
        discountStartDate: null,
        discountEndDate: null,
        nextBillingdate: null
      },
      createdAt: serverTimestamp(),
      updatedAt: serverTimestamp()
    });
    
    console.log('Client document created successfully');
    
    await setDoc(doc(db, 'salons', customDomain), {
      salonId: salonId,
      name: formData.salonName,
      status: formData.status || 'active',
      enabledModules: enabledModules,
      createdAt: serverTimestamp(),
      updatedAt: serverTimestamp()
    });
    
    console.log('Salon document created successfully');
    
    return { 
      id: customDomain, 
      salonId: salonId, 
      ...formData,
      authWarnings: []
    };
  } catch (error) {
    console.error('Error creating client configuration:', error);
    throw error;
  }
};

export const getAllClientConfigurations = async () => {
  try {
    const clientsSnapshot = await getDocs(collection(db, 'clients'));
    const clients = [];
    
    clientsSnapshot.forEach((doc) => {
      const clientData = doc.data();
      
      clients.push({
        id: doc.id,
        salonId: clientData.salonId || doc.id,
        name: clientData.name || 'Unnamed Client',
        status: clientData.status || 'active',
        enabledModules: clientData.enabledModules || {
          home: true,
          promotions: false,
          communications: false,
          bookings: false,
          reports: false,
          bespokeai: false
        },
        createdAt: clientData.createdAt,
        updatedAt: clientData.updatedAt
      });
    });
    
    return clients;
  } catch (error) {
    console.error('Error fetching client configurations:', error);
    throw error;
  }
};

export const getClientById = async (clientId) => {
  try {
    const clientDoc = await getDoc(doc(db, 'clients', clientId));
    
    if (!clientDoc.exists()) {
      throw new Error('Client not found in clients collection');
    }
    
    const clientData = clientDoc.data();
    const salonId = clientData.salonId || clientId;
    const adminUsers = await getAdminUsersBySalonId(salonId);
    
    return {
      id: clientId,
      salonId: salonId,
      name: clientData.name || 'Unnamed Client',
      status: clientData.status || 'active',
      enabledModules: clientData.enabledModules || {
        home: true,
        promotions: false,
        communications: false,
        bookings: false,
        reports: false,
        bespokeai: false
      },
      clientDomain: clientData.clientDomain || '',
      adminLoginURL: clientData.adminLoginURL || '',
      customerLoginURL: clientData.customerLoginURL || '',
      createdAt: clientData.createdAt,
      updatedAt: clientData.updatedAt,
      admins: adminUsers
    };
  } catch (error) {
    console.error('Error fetching client:', error);
    throw error;
  }
};

const getAdminUsersBySalonId = async (salonId) => {
  try {
    console.log('🔍 Querying salonUsers for salonId:', salonId);
    
    const usersQuery = query(
      collection(db, 'salonUsers'),
      where('salonId', '==', salonId)
    );
    
    const usersSnapshot = await getDocs(usersQuery);
    const adminUsers = [];
    
    usersSnapshot.forEach((doc) => {
      const userData = doc.data();
      console.log('📄 Found admin user document:', doc.id, userData);
      
      adminUsers.push({
        uid: userData.UID || doc.id,
        name: userData.name || 'Unknown',
        email: userData.email || 'No email',
        role: userData.role || 'admin',
        permissions: userData.permissions || {}
      });
    });
    
    console.log('✅ Total admin users found:', adminUsers.length);
    return adminUsers;
  } catch (error) {
    console.error('❌ Error fetching admin users:', error);
    return [];
  }
};

export const updateClientConfiguration = async (clientId, updates) => {
  try {
    const clientRef = doc(db, 'clients', clientId);
    await updateDoc(clientRef, {
      ...updates,
      updatedAt: serverTimestamp()
    });
    return true;
  } catch (error) {
    console.error('Error updating client configuration:', error);
    throw error;
  }
};

export const deleteClientConfiguration = async (clientId) => {
  try {
    await deleteDoc(doc(db, 'clients', clientId));
    return true;
  } catch (error) {
    console.error('Error deleting client configuration:', error);
    throw error;
  }
};

// ========================================
// ADMIN USER MANAGEMENT - MANUAL SETUP
// ========================================

/**
 * Creates a salonUsers Firestore record using a Firebase UID that the SuperAdmin
 * has already created in Firebase Authentication Console.
 * adminData: { name, email, firebaseUID, role, staffId? }
 */
export const addAdminToClient = async (clientId, adminData) => {
  // Step 1: Validate Firebase UID up-front
  if (!adminData.firebaseUID || adminData.firebaseUID.trim() === '') {
    throw new Error('Firebase UID is required. Please create the user in Firebase Authentication first and provide the UID.');
  }
  const firebaseUID = adminData.firebaseUID.trim();

  // Step 2: Fetch client document to resolve salonId
  const clientDocRef = doc(db, 'clients', clientId);
  const clientDoc = await getDoc(clientDocRef);
  if (!clientDoc.exists()) {
    throw new Error('Could not find client with ID: ' + clientId);
  }
  const clientDocData = clientDoc.data();
  const salonId = clientDocData.salonId || clientId;
  const resolvedClientId = clientDocData.clientDomain || clientId;

  // Step 3: Build the salonUsers record (mirrors existing record structure)
  const roleForUser = adminData.role || 'admin';
  const salonUserData = {
    UID: firebaseUID,
    uid: firebaseUID,
    clientId: resolvedClientId,
    email: adminData.email,
    name: adminData.name,
    role: roleForUser,
    salonId,
    fcmToken: '',
    fcmTokenUpdated: Timestamp.now(),
    notificationSettings: { emailNotifications: true, pushNotifications: true },
    permissions: DEFAULT_PERMISSIONS[roleForUser] || DEFAULT_PERMISSIONS.staff,
    createdAt: Timestamp.now(),
    updatedAt: Timestamp.now()
  };
  if (roleForUser === 'staff' && adminData.staffId) {
    salonUserData.staffId = adminData.staffId.trim();
  }

  // Step 4: Guard against overwriting an existing record for a different salon
  const salonUserDocRef = doc(db, 'salonUsers', firebaseUID);
  const existingDoc = await getDoc(salonUserDocRef);
  if (existingDoc.exists()) {
    const existing = existingDoc.data();
    const existingSalon = existing.salonId || existing.clientId || '(unknown salon)';
    throw new Error(
      `This Firebase UID is already registered to salon "${existingSalon}". ` +
      `Each admin account must be unique — please create a separate Firebase Authentication account for this salon.`
    );
  }

  await setDoc(salonUserDocRef, salonUserData);

  // Step 5: Verify
  const verifyDoc = await getDoc(salonUserDocRef);
  if (!verifyDoc.exists()) {
    throw new Error('Document creation failed — record not found after write.');
  }

  return { uid: firebaseUID, email: adminData.email, name: adminData.name, success: true };
};

export const updateAdminUser = async (adminUid, adminData) => {
  try {
    console.log('Updating admin user:', adminUid);

    const updates = {
      name: adminData.name,
      role: adminData.role,
      permissions: DEFAULT_PERMISSIONS[adminData.role] || DEFAULT_PERMISSIONS.staff,
      updatedAt: Timestamp.now()
    };

    if (adminData.role === 'staff' && adminData.staffId !== undefined) {
      updates.staffId = adminData.staffId || null;
    }

    await updateDoc(doc(db, 'salonUsers', adminUid), updates);
    
    console.log('Admin user updated successfully');
    return true;
  } catch (error) {
    console.error('Error updating admin user:', error);
    throw error;
  }
};

export const deleteAdminUser = async (adminUid) => {
  try {
    console.log('Deleting admin user:', adminUid);
    
    await deleteDoc(doc(db, 'salonUsers', adminUid));
    
    console.log('Admin user deleted successfully');
    return true;
  } catch (error) {
    console.error('Error deleting admin user:', error);
    throw error;
  }
};

// ========================================
// MODULE MANAGEMENT
// ========================================

export const getClientModules = async (clientId) => {
  try {
    const clientDoc = await getDoc(doc(db, 'clients', clientId));
    
    const DEFAULT_MODULES = {
      home: true,
      promotions: false,
      communications: false,
      bookings: false,
      reports: false,
      bespokeai: false,
      bookingsAI: false,
      communicationsAI: false,
      loyaltyAI: false,
      reportsAI: false
    };

    if (!clientDoc.exists()) {
      return DEFAULT_MODULES;
    }

    const clientData = clientDoc.data();
    // Merge with defaults so new AI fields are always present
    return { ...DEFAULT_MODULES, ...(clientData.enabledModules || {}) };
  } catch (error) {
    console.error('Error fetching client modules:', error);
    throw error;
  }
};

export const updateClientModules = async (clientId, modules) => {
  try {
    const clientRef = doc(db, 'clients', clientId);
    const clientDoc = await getDoc(clientRef);

    if (!clientDoc.exists()) {
      throw new Error('Client not found');
    }

    // Update clients document (primary source of truth)
    await updateDoc(clientRef, {
      enabledModules: modules,
      updatedAt: serverTimestamp()
    });

    // Keep salons document in sync so the ModuleContext salons-fallback also works
    const salonId = clientDoc.data().salonId || clientId;
    try {
      const salonRef = doc(db, 'salons', salonId);
      const salonDoc = await getDoc(salonRef);
      if (salonDoc.exists()) {
        await updateDoc(salonRef, { enabledModules: modules, updatedAt: serverTimestamp() });
      }
    } catch (syncErr) {
      // Non-fatal — clients document is already updated
      console.warn('Could not sync enabledModules to salons document:', syncErr.message);
    }

    return true;
  } catch (error) {
    console.error('Error updating client modules:', error);
    throw error;
  }
};

// ========================================
// URL MANAGEMENT
// ========================================

export const getClientUrls = async (clientId) => {
  try {
    const clientDoc = await getDoc(doc(db, 'clients', clientId));
    
    if (!clientDoc.exists()) {
      return {
        clientDomain: '',
        adminLoginURL: '',
        customerLoginURL: ''
      };
    }
    
    const clientData = clientDoc.data();
    return {
      clientDomain: clientData.clientDomain || '',
      adminLoginURL: clientData.adminLoginURL || '',
      customerLoginURL: clientData.customerLoginURL || ''
    };
  } catch (error) {
    console.error('Error fetching client URLs:', error);
    throw error;
  }
};

export const updateClientUrls = async (clientId, urls) => {
  try {
    const clientRef = doc(db, 'clients', clientId);
    const clientDoc = await getDoc(clientRef);
    
    if (!clientDoc.exists()) {
      throw new Error('Client not found');
    }
    
    const urlData = {
      clientDomain: urls.clientDomain || '',
      adminLoginURL: urls.adminLoginURL || '',
      customerLoginURL: urls.customerLoginURL || '',
      updatedAt: serverTimestamp()
    };
    
    await updateDoc(clientRef, urlData);
    return true;
  } catch (error) {
    console.error('Error updating client URLs:', error);
    throw error;
  }
};